import { Accordion, Card, CardContent, Grid, Icon, IconButton } from '@mui/material'
import SimpleTabList from 'components/list/SimpleTabList'
import i18n from 'simple-react-i18n'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useTitle from 'utils/customHook/useTitle'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import QualityAction from 'quality/actions/QualityAction'
import CampaignAction from 'campaign/actions/CampaignAction'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import StationAction from 'station/actions/StationAction'
import UserAction from 'administration/components/user/actions/UserAction'
import CityAction from 'referencial/components/city/actions/CityAction'
import ProgressCard from 'components/card/ProgressCard'
import CampaignPortletPanel from 'campaign/components/CampaignPortletPanel'
import useListIndexed from 'utils/customHook/useListIndexed'
import { getCampaignNameCell, getCampaignProgress, getCampaignsWithBookmarks, getProgressBar, getQualityCampaignIcon } from 'campaign/utils/CampaignUtils'
import useCampaignBookmarks from '../../../utils/customHook/useCampaignBookmarks'
import { push } from '@lagunovsky/redux-react-router'
import SimpleKeyFigurePanel from 'components/keyFigures/SimpleKeyFigurePanel'
import StationFilterFields from 'station/components/search/StationFilterFields'
import { STATION_QUALITOMETER_NAMES, STATION_TYPE, STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import { formatMilliers, getI18nTitleData, normalizeString } from 'utils/StringUtil'
import { groupBy, isNil, isObject, isUndefined, keys, omit, orderBy, take, uniq } from 'lodash'
import NetworkAction from 'referencial/components/network/actions/NetworkAction'
import { ALIGN, CardTable, NewTable, sortRows, WrapperAccordionDetails } from 'components/datatable/NewTable'
import { favOrange } from 'utils/constants/ColorTheme'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { getDate, getTimestamp } from 'utils/DateUtil'
import { FR, nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import WifiIcon from '@mui/icons-material/Wifi'
import PropTypes from 'prop-types'
import useApplicationSetting, { intParser } from 'utils/customHook/useApplicationSetting'
import { DEFAULT_CONTRIBUTOR_TYPE } from 'administration/components/user/constants/UserConstants'
import { AccordionTitle } from 'components/styled/Accordions'
import { CardTitle } from 'components/card/NewCard'
import CartographyPanel from 'components/map/CartographyPanel'
import { hasLocalisationStation } from 'utils/StationUtils'
import useActions from 'utils/customHook/useActions'
import AddQualitoStepper from './addQualito/AddQualitoStepper'
import AdministrationAction from 'administration/actions/AdministrationAction'
import ColorConstant from 'components/constants/ColorConstant'
import MessageCard from 'components/card/MessageCard'
import useAbortController from 'utils/customHook/useAbortController'
import CircularProgress from '@mui/material/CircularProgress'
import { hasValue } from 'utils/NumberUtil'
import FractionAction from 'referencial/components/fraction/actions/FractionAction'
import ExportFileModal from 'components/modal/ExportFileModal'
import useBoolean from 'utils/customHook/useBoolean'
import ExportAction from 'export/actions/ExportAction'
import { formatData } from 'utils/ExportDataUtil'
import ToastrAction from 'toastr/actions/ToastrAction'
import Select from 'components/forms/Select'
import { convertCoordinates, PROJECTIONS } from 'utils/mapUtils/CoordinateUtils'
import moment from 'moment'

const TAB = {
    LIST: 'LIST',
    MAP: 'MAP',
    REFERENT: 'REFERENT',
}

const CampaignsPanel = () => {
    const dispatch = useDispatch()
    const {
        contacts,
        qualityCampaignsProgress,
        qualityCampaigns,
        accountUser,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
        qualityCampaignsProgress: store.QualityReducer.qualityCampaignsProgress,
        qualityCampaigns: store.QualityReducer.qualityCampaigns,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const indexedContacts = useListIndexed(contacts, 'id')
    const { bookmarkIds } = useCampaignBookmarks(STATION_TYPE_NAME.quality)

    useEffect(() => {
        dispatch(CampaignAction.fetchQualityCampaigns())
    }, [dispatch])

    const orderedCampaigns = useMemo(() => {
        const filteredCampaigns = orderBy(qualityCampaigns.filter(c => c.statut === 2), c => getTimestamp(c.beginningApplication), 'desc')
        return getCampaignsWithBookmarks(qualityCampaigns, [
            ...filteredCampaigns.filter(c => c.contactCode === accountUser.contactCode),
            ...filteredCampaigns.filter(c => c.contactCode !== accountUser.contactCode),
        ], bookmarkIds)
    }, [qualityCampaigns, accountUser, bookmarkIds])

    const {
        controllerRef,
        initController,
    } = useAbortController()

    useEffect(() => {
        initController()
        if (orderedCampaigns.length) {
            dispatch(CampaignAction.getCampaignProgressSequentially('quality', take(orderedCampaigns, 3), controllerRef.current.signal))
        }
    }, [orderedCampaigns, controllerRef])

    const formattedCampaigns = orderedCampaigns.map(c => {
        const {
            progress = 0,
            progressTotal = 0,
            nbStation,
            id,
        } = qualityCampaignsProgress.find(p => p.id === c.id) || {}

        const progressValue = getCampaignProgress(progress, progressTotal)

        return {
            id: c.id,
            campaignColor: {
                color: ColorConstant.ORANGE,
            },
            campaignIcon: getQualityCampaignIcon(c.campaignType, 'small'),
            name: getCampaignNameCell(c.name, bookmarkIds.includes(c.id)),
            startDate: { value: c.beginningApplication, sortValue: getTimestamp(c.beginningApplication, 'DD/MM/YYYY') },
            endDate: { value: c.endingApplication, sortValue: getTimestamp(c.endingApplication, 'DD/MM/YYYY') },
            referent: indexedContacts[c.contactCode]?.mnemonique ?? indexedContacts[c.contactCode]?.name,
            contactCode: c.contactCode,
            nbStations: nbStation,
            progression: hasValue(id) && getProgressBar(progress, progressTotal, progressValue, i18n.analyseOn, i18n.analysisOn) || <CircularProgress size={25} />,
        }
    })

    const headerIcon = (
        <IconButton
            sx={{
                color: '#000',
                fontSize: '1.8rem',
                padding: '1px',
            }}
            onClick={() => dispatch(push('/quality/campaign'))}
        >
            <Icon fontSize='inherit'>nature_people</Icon>
        </IconButton>
    )

    return (
        <CampaignPortletPanel
            campaigns={formattedCampaigns}
            headers={[
                { key: 'campaignColor', value: headerIcon, width: 26, sticky: ALIGN.LEFT },
                { key: 'campaignIcon', value: '' }, { key: 'name', value: i18n.campaign },
                'nbStations', 'startDate', 'endDate', 'referent', 'progression',
            ]}
            stationType={STATION_TYPE_NAME.quality}
        />
    )
}

const StatisticsPanel = () => {
    const dispatch = useDispatch()
    const {
        qualityKeyfigures,
        qualitometersListSpecific,
    } = useSelector(store => ({
        qualityKeyfigures: store.QualityReducer.qualityKeyfigures,
        qualitometersListSpecific: store.QualityReducer.qualitometersListSpecific,
    }), shallowEqual)

    useEffect(() => {
        dispatch(QualityAction.fetchKeyFigures())
    }, [dispatch])

    return (
        <SimpleKeyFigurePanel
            data={[
                ...qualityKeyfigures.map(k => ({ ...k, value: formatMilliers(k.value) })),
                { title: i18n.stations, value: formatMilliers(qualitometersListSpecific.length) },
            ]}
            showAllValues
        />
    )
}

const LegendPanel = () => (
    <Grid container spacing={1} style={{ padding: '4px' }} alignItems='center'>
        <Grid size='auto'>
            <WifiIcon />
        </Grid>
        <Grid size='auto'>
            {i18n.automaticUpdate}
        </Grid>
    </Grid>
)

const QualitometersTable = ({
    qualitometers = [],
}) => {
    const dispatch = useDispatch()
    const filterResults = useSelector(store => store.StationReducer.filterResults, shallowEqual)
    const selectedSearchValues = useSelector(store => store.AdministrationReducer.selectedSearchValues, shallowEqual)

    const { quality: searchValues } = selectedSearchValues
    const initialSort = searchValues?.sort

    const onTableSort = (qualitos, sort) => {
        dispatch(AdministrationAction.setSelectedSearchValues('quality', { stations: sortRows(qualitos, sort), sort }))
    }

    useEffect(() => {
        onTableSort(qualitometers, initialSort)
    }, [qualitometers])

    const additionalHeaders = keys(filterResults[0] ?? {}).filter(key => key !== 'id')

    const headers = useMemo(() => [
        { key: 'bookmark', value: '' },
        { key: 'origin', value: '' },
        'code',
        { key: 'internalReferences', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.internalReferencesHeader}</span>) },
        { key: 'cityCode', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.cityCodeHeader}</span>) },
        'cityLabel',
        { key: 'sliceName', value: i18n?.name ?? '' },
        'creation',
        'address',
        'operations',
        'begin',
        'end',
        'stationTypeLabel',
    ], [])

    return (
        <CardTable
            title={i18n.watchpoints}
            actions={[{
                icon: 'info_outlined',
                tooltip: <LegendPanel />,
            }]}

            rows={qualitometers}
            headers={[...headers, ...additionalHeaders]}
            onClickRow={({ id }) => dispatch(push(`/station/quality/${id}`))}
            rowsPerPageOptions={nbPerPageLabel}
            defaultSort={initialSort}
            onSort={(sort) => onTableSort?.(qualitometers, sort)}
        />
    )
}

QualitometersTable.propTypes = {
    qualitometers: PropTypes.arrayOf(PropTypes.shape({})),
}

const QualitometersCarto = ({
    qualitometers = [],
}) => {
    const filteredQualitometers = qualitometers.filter(q => q && (hasLocalisationStation(q) || q.townCode))
    return (
        <Card>
            <CardTitle
                title={(
                    <>
                        <span style={{ fontSize: '1.4rem', fontWeight: 'bold' }}>{i18n.watchpoints}</span>
                        <span style={{ fontSize: '1.4rem', fontWeight: 'normal' }}>&nbsp;{`(${qualitometers.length} ${getI18nTitleData(i18n.element, i18n.elements, qualitometers)})`}</span>
                    </>
                )}
            />
            <CardContent padding={0}>
                <CartographyPanel
                    layers={['STATIONS_POINTS']}
                    componentType='quality'
                    stationsPoints={filteredQualitometers}
                    stationsPanelTitle={i18n.stations}
                    heightToSubstract={450}
                    styleContainer={{ borderRadius: '0 0 4px 4px' }}
                />
            </CardContent>
        </Card>
    )
}

QualitometersCarto.propTypes = {
    qualitometers: PropTypes.arrayOf(PropTypes.shape({})),
}

const QualitometersByReferent = ({
    qualitometers = [],
}) => {
    const dispatch = useDispatch()
    const contributorLinks = useSelector(store => store.StationReducer.contributorLinks, shallowEqual)
    const contributorsIndex = useSelector(store => store.ContributorReducer.contributorsIndex, shallowEqual)
    const filterResults = useSelector(store => store.StationReducer.filterResults, shallowEqual)

    const headers = useMemo(() => [{ key: 'bookmark', value: '' }, { key: 'origin', value: '' }, 'code', 'internalReferences', 'cityCode', 'cityLabel', { key: 'sliceName', value: i18n?.name ?? '' }, 'creation', 'address', 'operations', 'begin', 'end', 'stationTypeLabel'], [])

    const additionalHeaders = keys(filterResults[0] ?? {}).filter(key => key !== 'id')

    const indexedQualitometers = useListIndexed(qualitometers, 'id')
    const groupedLinks = groupBy(contributorLinks.filter(l => !isNil(indexedQualitometers[l.idStation])), 'idContributor')
    const keysLinks = keys(groupedLinks)

    return (
        <Grid container spacing={1} direction='column' sx={{ padding: 1 }}>
            {keysLinks.length === 0 && (<MessageCard>{i18n.noDataToDisplay}</MessageCard>)}
            {keysLinks.map((key, i) => {
                const subQualitometers = groupedLinks[key].map(l => indexedQualitometers[l.idStation])
                return (
                    <Grid key={key}>
                        <Accordion defaultExpanded={i === 0}>
                            <AccordionTitle
                                title={(
                                    <>
                                        <span style={{ fontSize: '1.4rem' }}>{key !== 'undefined' ? contributorsIndex[key]?.name : i18n.unknown}</span>
                                        <span style={{ fontSize: '1.4rem', fontWeight: 'normal' }}>&nbsp;{`(${subQualitometers.length} ${getI18nTitleData(i18n.element, i18n.elements, subQualitometers)})`}</span>
                                    </>
                                )}
                            />
                            <NewTable
                                rows={subQualitometers}
                                headers={[...headers, ...additionalHeaders]}
                                onClickRow={({ id }) => dispatch(push(`/station/quality/${id}`))}
                                WrapperComponent={WrapperAccordionDetails}
                            />
                        </Accordion>
                    </Grid>
                )
            })}
        </Grid>
    )
}

QualitometersByReferent.propTypes = {
    qualitometers: PropTypes.arrayOf(PropTypes.shape({})),
}

const ExportModal = ({
    isOpen,
    onClose = () => {},
    filteredQualitometers,
}) => {
    const dispatch = useDispatch()
    const {
        qualitometersListSpecific,
    } = useSelector(store => ({
        qualitometersListSpecific: store.QualityReducer.qualitometersListSpecific,
    }), shallowEqual)
    const citiesIndex = useSelector(store => store.CityReducer.citiesIndex, shallowEqual)

    const [projection, setProjection] = useState()

    const getLocalisation = useCallback(qualito => {
        if (hasLocalisationStation(qualito)) return { x: qualito.x, y: qualito.y, projection: qualito.projection }

        const city = citiesIndex[qualito.townCode]
        if (city && (!city.countryCode || city.countryCode === FR) && hasLocalisationStation(city)) {
            return { x: city.x, y: city.y, projection: city.projection }
        }

        return {}
    }, [citiesIndex])

    const onExport = (type) => {
        const qualitometers = qualitometersListSpecific.filter(q => filteredQualitometers?.some(fQ => fQ.id === q.id)).map(qualito => {
            const withDesignation = !isNil(qualito.designation) && qualito.code.includes('X') && qualito.stationType == STATION_TYPE.POINT_EAU_SOUTERRAINE
            const loc = getLocalisation(qualito)
            const usedLoc = !isNil(projection) ? convertCoordinates(loc.projection, projection, loc) : loc
            return {
                code: { value: withDesignation ? `${qualito.code}/${qualito.designation}` : qualito.code, cellType: 'string' },
                internalReferences: { value: qualito.internalReferences, cellType: 'string' },
                cityCode: { value: qualito.townCode, cellType: 'string' },
                cityLabel: citiesIndex[qualito.townCode]?.name ?? '',
                name: qualito.name,
                creation: { value: getDate(qualito.creationDate), format: 'dd/MM/yyyy', cellType: 'date' },
                address: qualito.address,
                operations: { value: qualito.operationCount, format: '0', cellType: 'number' },
                begin: { value: getDate(qualito.historyStart), format: 'dd/MM/yyyy', cellType: 'date' },
                end: { value: getDate(qualito.historyEnd), format: 'dd/MM/yyyy', cellType: 'date' },
                stationTypeLabel: STATION_QUALITOMETER_NAMES.find(t => `${t.code}` === qualito.stationType)?.name,
                x: { value: usedLoc.x, format: '0.0000000000', cellType: 'number' },
                y: { value: usedLoc.y, format: '0.0000000000', cellType: 'number' },
                projection: { value: projection ?? usedLoc.projection, format: '0', cellType: 'number' },
            }
        })

        if (qualitometers.length) {
            const exportData = [
                {
                    ...qualitometers[0],
                    headers: keys(qualitometers[0]),
                },
                ...qualitometers.slice(1),
            ]
            dispatch(ExportAction.export(formatData(exportData), type, i18n.qualitometers))
        } else {
            dispatch(ToastrAction.warning(i18n.noDataToExport))
        }
    }

    return (
        <ExportFileModal
            open={isOpen}
            onClose={onClose}
            data={[
                {
                    name: i18n.resultsTable,
                    formats: [{
                        type: i18n.excelFile,
                        callback: () => onExport('xlsx'),
                    }, {
                        type: i18n.csvFile,
                        callback: () => onExport('csv'),
                    }],
                    settingsContent: (
                        <Select
                            label={i18n.coordinateConversion}
                            value={projection}
                            onChange={setProjection}
                            options={PROJECTIONS}
                            keyLabel='name'
                            keyValue='sandreCode'
                        />
                    ),
                },
            ]}
        />
    )
}

ExportModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    filteredQualitometers: PropTypes.arrayOf(PropTypes.shape({})),
}

const QualitometersTabs = () => {
    const {
        qualitometersListSpecific,
        networkLinks,
        contributorLinks,
        filterResults: filterResultsProps,
        accountUser,
    } = useSelector(store => ({
        qualitometersListSpecific: store.QualityReducer.qualitometersListSpecific,
        networkLinks: store.QualityReducer.networkLinks,
        contributorLinks: store.StationReducer.contributorLinks,
        filterResults: store.StationReducer.filterResults,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)
    const networks = useSelector(store => store.NetworkReducer.networks, shallowEqual)
    const userBookmarks = useSelector(store => store.UserReducer.userBookmarks, shallowEqual)
    const citiesIndex = useSelector(store => store.CityReducer.citiesIndex, shallowEqual)
    const globalResearch = useSelector(store => store.HomeReducer.globalResearch, shallowEqual)
    const selectedSearchValues = useSelector(store => store.AdministrationReducer.selectedSearchValues, shallowEqual)

    const referentType = useApplicationSetting('contributorTypeAdministrator', intParser) ?? DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR
    const operatorType = useApplicationSetting('contributorTypeOperator', intParser) ?? DEFAULT_CONTRIBUTOR_TYPE.OPERATOR

    const [isOpen, setIsOpen] = useState(false)
    const {
        value: isExportOpen,
        setTrue: onOpenExport,
        setFalse: onCloseExport,
    } = useBoolean(false)

    const [filters, setFilters] = useState(() => {
        const {
            filter: filterCode = -1,
            filterResults = [],
            searchValue = '',
        } = selectedSearchValues.quality || {}
        return {
            filter: filterCode,
            filterResults,
            searchValue: globalResearch || searchValue,
        }
    })

    const bookmarks = useMemo(() => {
        return userBookmarks.filter(b => b.stationType === 3).map(b => b.identifiant)
    }, [userBookmarks])

    const networklist = useMemo(() => {
        const now = moment().valueOf()
        return uniq(networkLinks.filter(n => !n.endDate || n.endDate >= now).map(l => l.idNetwork)).map(id => networks.find(n => id === n.id)).filter(c => !isUndefined(c))
    }, [networkLinks, networks])

    const getLocalisation = useCallback(qualito => {
        if (hasLocalisationStation(qualito)) return { x: qualito.x, y: qualito.y, projection: qualito.projection }

        const city = citiesIndex[qualito.townCode]
        if (city && (!city.countryCode || city.countryCode === FR) && hasLocalisationStation(city)) {
            return { x: city.x, y: city.y, projection: city.projection }
        }

        return {}
    }, [citiesIndex])

    const formattedQualitometers = useMemo(() => {
        return qualitometersListSpecific.map(qualito => {
            const isFav = bookmarks.some(code => code === qualito.code)
            const withDesignation = !isNil(qualito.designation) && qualito.code.includes('X') && qualito.stationType == STATION_TYPE.POINT_EAU_SOUTERRAINE
            const loc = getLocalisation(qualito)
            return {
                id: qualito.id,
                bookmark: { value: isFav ? <StarRoundedIcon sx={{ color: favOrange }} /> : undefined, noPadding: true },
                origin: !isNil(qualito.jobExecutionId) && (<WifiIcon />),
                code: withDesignation ? `${qualito.code}/${qualito.designation}` : qualito.code,
                internalReferences: qualito.internalReferences,
                cityCode: { value: qualito.townCode, align: ALIGN.RIGHT },
                cityLabel: citiesIndex[qualito.townCode]?.name ?? '',
                sliceName: { value: qualito.name?.slice(0, 60) ?? '', tooltip: qualito.name, sortValue: qualito.name },
                creation: { value: getDate(qualito.creationDate), sortValue: qualito.creationDate },
                address: { value: qualito.address?.slice(0, 60) ?? '', tooltip: qualito.address, sortValue: qualito.address },
                operations: qualito.operationCount,
                begin: { value: getDate(qualito.historyStart), sortValue: qualito.historyStart },
                end: { value: getDate(qualito.historyEnd), sortValue: qualito.historyEnd },
                stationTypeLabel: STATION_QUALITOMETER_NAMES.find(t => `${t.code}` === qualito.stationType)?.name,
                colorLine: qualito.closeDate ? 'grey' : undefined,

                // CARTO
                name: qualito.name,
                townCode: qualito.townCode,
                typeName: qualito.typeName,
                stationType: qualito.stationType,
                x: loc.x,
                y: loc.y,
                projection: loc.projection,
            }
        })
    }, [bookmarks, citiesIndex, getLocalisation, qualitometersListSpecific])

    const filteredQualitometers = useMemo(() => {
        const {
            searchValue,
            filter = -1,
            filterResults = [],
            network,
            referentIds = [],
            operatorIds = [],
        } = filters

        const filterStation = (() => {
            if (filter === -1) return formattedQualitometers
            return filterResults.map(qr => {
                const qualito = formattedQualitometers.find(q => q.id === qr.id)
                const result = filterResultsProps.find(fr => fr.id === qr.id)
                return {
                    ...result,
                    ...qualito,
                }
            })
        })()

        const now = moment().valueOf()
        const filterOperator = operatorIds.length === 0 ?
            filterStation :
            filterStation.filter(s => contributorLinks.filter(l => l.contributorType === operatorType && operatorIds.includes(l.idContributor) && (l.endDate >= now || !l.endDate)).some(l => l.idStation === s.id))
        const filterReferent = referentIds.length === 0 ?
            filterOperator :
            filterOperator.filter(s => contributorLinks.filter(l => l.contributorType === referentType && referentIds.includes(l.idContributor) && (l.endDate >= now || !l.endDate)).some(l => l.idStation === s.id))

        const filteredNetworkLinks = networkLinks.filter(l => network === l.idNetwork && (!l.endDate || l.endDate >= now))
        const filterNetwork = !isNil(network) ? filterReferent.filter(q => filteredNetworkLinks.some(l => q.id === l.idStation)) : filterReferent

        const normalizeSearch = normalizeString(searchValue)
        const keysToSearch = keys(omit(filterStation[0], ['id', 'bookmark', 'origin', 'colorLine', 'townCode', 'x', 'y', 'projection']))
        return normalizeSearch ? filterNetwork.filter(q => keysToSearch.some(key => normalizeString(isObject(q[key]) ? q[key].value : q[key]).includes(normalizeSearch))) : filterNetwork
    }, [contributorLinks, filterResultsProps, filters, formattedQualitometers, networkLinks, operatorType, referentType])

    useActions(() => {
        const exportAction = {
            exportList: [{
                onClick: onOpenExport,
                label: i18n.excel,
            }],
        }
        if (accountUser.isAdmin === '1' || accountUser.metadata === '1') {
            return {
                new: () => setIsOpen(true),
                ...exportAction,
            }
        }
        return exportAction
    }, [accountUser])

    return (
        <>
            <SimpleTabList
                defaultTab={TAB.LIST}
                tabs={[
                    {
                        constant: TAB.LIST,
                        label: i18n.table,
                        icon: 'list',
                    },
                    {
                        constant: TAB.MAP,
                        label: i18n.map,
                        icon: 'map',
                    },
                    {
                        constant: TAB.REFERENT,
                        label: i18n.contributor,
                        icon: 'person',
                    },
                ]}
                noPadding
                headerComponent={(
                    <StationFilterFields
                        defaultFilter={filters}
                        onValidate={setFilters}

                        stationType={STATION_TYPE_NAME.quality}
                        contributorLinks={contributorLinks}
                        stations={qualitometersListSpecific}
                        networklist={networklist}
                    />
                )}
            >
                {tab => (
                    <>
                        {tab === TAB.LIST && (<QualitometersTable qualitometers={filteredQualitometers} />)}
                        {tab === TAB.MAP && (<QualitometersCarto qualitometers={filteredQualitometers} />)}
                        {tab === TAB.REFERENT && (<QualitometersByReferent qualitometers={filteredQualitometers} />)}
                    </>
                )}
            </SimpleTabList>
            <AddQualitoStepper isOpen={isOpen} setIsOpen={setIsOpen} />
            <ExportModal
                isOpen={isExportOpen}
                onClose={onCloseExport}
                filteredQualitometers={filteredQualitometers}
            />
        </>
    )
}

const QualitometersDashboardApp = () => {
    const dispatch = useDispatch()

    useTitle(() => [{
        title: i18n.quality,
        href: 'quality',
    }, {
        title: i18n.dashboard,
        href: 'quality',
    }], [])

    const {
        isLoaded,
        progress,
    } = useProgressDispatch(() => [
        dispatch(QualityAction.fetchQualitometersListSpecific()),
        dispatch(StationAction.fetchFilters()),
        dispatch(UserAction.fetchBookmarks()),
        dispatch(CityAction.fetchCities()),
    ], [])

    useEffect(() => {
        dispatch(StationAction.fetchAllContributors(STATION_TYPE_CONSTANT.quality))
        dispatch(QualityAction.fetchQualityNetworkLinks())
        dispatch(NetworkAction.fetchNetworks())
        dispatch(FractionAction.fetchFractions())
    }, [dispatch])

    return (
        <Grid container spacing={1}>
            <Grid size={9}>
                <CampaignsPanel />
            </Grid>
            <Grid size={3}>
                <StatisticsPanel />
            </Grid>
            {!isLoaded && (
                <Grid size={12}>
                    <ProgressCard progress={progress} withMessage />
                </Grid>
            )}
            {isLoaded && (
                <Grid size={12}>
                    <QualitometersTabs />
                </Grid>
            )}
        </Grid>
    )
}

export { QualitometersDashboardApp, CampaignsPanel }